import { authService } from '@/services/auth';

/**
 * Bearer credential for authenticated Strapi API calls.
 *
 * Returns the signed-in user's Keycloak access token (their own session JWT),
 * which the backend's `keycloak-jwt` content-API auth strategy accepts. This
 * REPLACES the former shared `GATSBY_STRAPI_TOKEN` that shipped inside the
 * browser bundle — a static, full-access credential anyone could read from the
 * JS or off the Network tab. A user's own JWT is theirs, short-lived, and safe
 * to carry client-side (it is the same token announcements/terms already send).
 *
 * Returns '' (NOT undefined) when no session is active. This is critical:
 * callers do `Bearer ${getStrapiToken()}`, and `Bearer undefined` is REJECTED
 * with 401 by the backend even on public/anonymous endpoints (value-lists,
 * app-countries), whereas `Bearer ` (empty) is treated as anonymous → 200. So
 * returning '' keeps pre-login / pre-auth-init fetches working — e.g. the
 * invitation-accept landing page a logged-out invitee opens from the email.
 * Regression fixed 2026-06-11 (ticket #955); before this it sent the literal
 * string "undefined" and broke those flows on every environment.
 */
export const getStrapiToken = () => authService.getAuthState()?.token || '';

/**
 * Same credential, refreshed first if it is about to expire. Use for calls the
 * backend verifies strictly and that can fire long after the last refresh tick
 * (e.g. the Tableau embed JWT, minted when a dashboard tab opens).
 */
export const getFreshStrapiToken = async () => {
  try {
    await authService.refreshToken();
  } catch {
    // Fall through with whatever token we hold; the backend decides.
  }
  return getStrapiToken();
};
